import { useMutation } from "@apollo/client";
import { useEffect, useMemo, useRef, useState } from "react";
import { shallowEqual } from "react-redux";
import { selectAllergens, selectAppState, selectOpenedDetailDishTypeId } from "slices/appStateSlice";
import {
	selectActualSpotlightDishTypeIds,
	selectAvailableDishTypesIds,
	selectFilteredDishTypeIds,
} from "slices/dishesSlice";
import { selectFridgeLightingMode } from "slices/fridgeLightingSlice";
import { selectActiveLocalTransaction } from "slices/transactionSlice";
import { LightingModeType } from "types";
import { AppState } from "types/mainTypes";
import { getTabletType, TabletTypeEnum } from "utils/browser/localStorage";
import { SWITCH_FRIDGE_MODE_MUTATION, TOGGLE_SPOTLIGHT_MODE_MUTATION } from "utils/gqlOperations";
import { computeDesiredLighting } from "utils/lightingConvergence";
import { createDefaultLight, createSpotlightLight } from "utils/lightingUtils";
import { log } from "utils/logger";
import { useFridgeInformation } from "./useFridgeInformation";
import { useAppSelector } from "./utilsHooks/storeHooks";

// ──────────────────────────────────────────────────────────────────────────────────────────────
// TEMP diagnostic suite. Traces every converger effect run, desired-state change, optimistic-ref
// clear, and mutation fire so a single capture proves the two re-fire hypotheses:
//
//   P1 (lights blink): desiredMode flaps ACTIVE↔STAND_BY. The `DESIRED-CHANGE … drivers=…` lines
//       name WHICH input flipped (appState idle-presence vs the transaction slot).
//   P2 (redundant mutations): a transaction passing through Finished (desiredKind=be-authoritative)
//       — or any StandBy dip — clears the sent-refs, so the next fe-driven tick re-issues an
//       IDENTICAL command. `FIRE-* … redundant=true` marks that (target equals the last command we
//       actually put on the wire), and the preceding `REF-CLEARED … cause=…` line attributes it.
//
// Capture the console, save to a .log, then run:  node scripts/analyze-lighting-log.mjs <file>
// Grep tag: "[lighting-converge]". Remove this whole block + LIGHTING_DEBUG once the fix lands.
//
// Typed as `boolean` (not literal `true`) so the `if (LIGHTING_DEBUG)` guards aren't flagged as
// always-true conditions and so the flag can be flipped without code changes.
const LIGHTING_DEBUG: boolean = true;

const fmtIds = (ids: readonly string[] | null): string => (ids == null ? "null" : `[${ids.join(",")}]`);

// Emit ONE flat `key=value` line per event (no object arg) so the console never collapses fields
// behind "…" and so scripts/analyze-lighting-log.mjs can parse it by splitting on spaces. Values
// must therefore never contain spaces (arrays are joined with commas, no spaces).
const lcLog = (event: string, fields: Record<string, string | number | boolean | null>): void => {
	if (!LIGHTING_DEBUG) {
		return;
	}
	const parts = Object.entries(fields)
		.map(([key, value]) => `${key}=${value}`)
		.join(" ");
	log(`[lighting-converge] ${event} ${parts}`, "info");
};

// How long `appState` must stay IDLE before the lights are allowed to dim to StandBy.
const STANDBY_IDLE_DEBOUNCE_MS = 25_000;

// P1 blink fix. `react-idle-timer` presence flaps IDLE↔ACTIVE, and dimming to StandBy on every idle
// blip made the fridge lights visibly blink (confirmed in production logs: DESIRED-CHANGE with
// drivers=appState driving most ACTIVE↔STAND_BY flaps). Gate the IDLE→StandBy transition behind a
// sustained-idle timer: any activity brightens immediately, but the lights only dim once idle has
// held for STANDBY_IDLE_DEBOUNCE_MS. Scoped to the lighting decision so other AppState.IDLE
// consumers (drawer / filter-modal auto-close) keep reacting to idle promptly.
const useDimGatedAppState = (appState: AppState, debounceMs: number): AppState => {
	// Init to the current appState: the debounce applies to active→idle TRANSITIONS (the flap), not
	// to the initial mount. If the app mounts already idle, dimming straight away is correct — it is
	// not a flap.
	const [gatedAppState, setGatedAppState] = useState<AppState>(appState);

	useEffect(() => {
		if (appState !== AppState.IDLE) {
			setGatedAppState(appState); // brighten immediately on any activity
			return;
		}
		const timer = setTimeout(() => setGatedAppState(AppState.IDLE), debounceMs);
		return () => {
			clearTimeout(timer);
		};
	}, [appState, debounceMs]);

	return gatedAppState;
};

// `standbyIdleDebounceMs` is injectable so tests can disable the debounce (0 = dim immediately);
// production callers use the default sustained-idle window.
export const useConvergeLighting = (standbyIdleDebounceMs: number = STANDBY_IDLE_DEBOUNCE_MS) => {
	const { id: fridgeId } = useFridgeInformation();
	// `loading` is a secondary suppression while a mutation is genuinely in flight; the
	// optimistic refs below are the primary guard against subscription-echo re-fires.
	const [setMode, { loading: modeLoading }] = useMutation(SWITCH_FRIDGE_MODE_MUTATION);
	const [setSpotlight, { loading: spotlightLoading }] = useMutation(TOGGLE_SPOTLIGHT_MODE_MUTATION);

	const appState = useAppSelector(selectAppState);
	const transaction = useAppSelector(selectActiveLocalTransaction);
	const openDetailDishTypeId = useAppSelector(selectOpenedDetailDishTypeId);
	const allergens = useAppSelector(selectAllergens);
	const filteredDishTypeIds = useAppSelector(selectFilteredDishTypeIds);
	const actualMode = useAppSelector(selectFridgeLightingMode);
	const actualSpotlightDishTypeIds = useAppSelector(selectActualSpotlightDishTypeIds);
	const availableDishTypeIds = useAppSelector(selectAvailableDishTypesIds);

	const hasTransaction = transaction != null;
	const transactionState = transaction?.state ?? null;

	// Debounced view of appState for the dim decision (see useDimGatedAppState). The raw `appState`
	// is still logged below so the diagnostics show the debounce holding (appState=idle while
	// appStateGated=active → no flap).
	const appStateForLighting = useDimGatedAppState(appState, standbyIdleDebounceMs);

	// "What we last sent and are still waiting for the BE to echo back." Written SYNCHRONOUSLY
	// before each mutation so that a re-run of this effect inside the echo round-trip — the BE
	// streams many subscription frames while e.g. the fridge door is left open during an order —
	// does NOT re-fire the identical mutation. Without this guard the converger spammed
	// SWITCH_FRIDGE_MODE_MUTATION for the whole open-door warning window (~60s). This restores the
	// optimistic-write protection the legacy useFridgeLights/MealList effects had before the
	// converger refactor dropped it.
	const sentModeRef = useRef<LightingModeType | null>(null);
	const sentSpotlightRef = useRef<readonly string[] | null>(null);

	// ── TEMP diagnostic state (additive; never read by the production guard above) ──────────────
	// Stable per-instance id + run counter: detect remount (id changes / MOUNT-UNMOUNT churn).
	const instanceIdRef = useRef<string | null>(null);
	if (instanceIdRef.current === null) {
		instanceIdRef.current = `lc-${Math.random().toString(36).slice(2, 8)}`;
	}
	const runCountRef = useRef(0);
	// "Last command actually put on the wire", NEVER cleared. A fire whose target equals this is
	// `redundant` — we already commanded the fridge to this exact state. This is the airtight P2
	// signal, independent of the production refs that the be-authoritative/StandBy dips wipe.
	const lastIssuedModeRef = useRef<LightingModeType | null>(null);
	const lastIssuedSpotlightRef = useRef<readonly string[] | null>(null);
	// Previous desired signature + raw inputs, to emit DESIRED-CHANGE with the driving input (P1).
	const prevDesiredSigRef = useRef<string | null>(null);
	const prevInputsRef = useRef<Record<string, string> | null>(null);

	useEffect(() => {
		lcLog("MOUNT", { id: instanceIdRef.current ?? "?" });
		return () => {
			lcLog("UNMOUNT", { id: instanceIdRef.current ?? "?" });
		};
	}, []);

	const desired = useMemo(
		() =>
			computeDesiredLighting({
				appState: appStateForLighting,
				hasTransaction: hasTransaction,
				transactionState: transactionState,
				openDetailDishTypeId: openDetailDishTypeId,
				allergens: allergens,
				filteredDishTypeIds: filteredDishTypeIds,
			}),
		[appStateForLighting, hasTransaction, transactionState, openDetailDishTypeId, allergens, filteredDishTypeIds],
	);

	useEffect(() => {
		const isMaster = getTabletType() === TabletTypeEnum.Master;
		if (!isMaster) {
			return;
		}
		if (fridgeId == null) {
			return;
		}

		runCountRef.current += 1;
		const run = runCountRef.current;

		// Display forms of the desired state (computed once, reused for tick + change detection).
		const desiredMode = desired.kind === "fe-driven" ? desired.mode : "-";
		const desiredSpotlight = desired.kind === "fe-driven" ? fmtIds(desired.spotlightDishTypeIds) : "-";
		const desiredSig = `${desired.kind}|${desiredMode}|${desiredSpotlight}`;

		// Snapshot taken BEFORE the clear/fire logic below mutates the refs, so `sentMode/Spotlight`
		// here is the value at effect entry.
		lcLog("tick", {
			fridge: fridgeId,
			id: instanceIdRef.current ?? "?",
			run: run,
			appState: appState,
			appStateGated: appStateForLighting,
			hasTx: hasTransaction,
			txState: String(transactionState),
			desiredKind: desired.kind,
			desiredMode: desiredMode,
			desiredSpotlight: desiredSpotlight,
			actualMode: String(actualMode),
			actualSpotlight: fmtIds(actualSpotlightDishTypeIds),
			sentMode: String(sentModeRef.current),
			sentSpotlight: fmtIds(sentSpotlightRef.current),
			modeLoading: modeLoading,
			spotlightLoading: spotlightLoading,
			availableCount: availableDishTypeIds.size,
		});

		// DESIRED-CHANGE: emit only when the desired signature actually changed, naming which raw
		// input(s) flipped — this is the P1 proof (e.g. drivers=appState:IDLE->ACTIVE for a blink).
		const curInputs: Record<string, string> = {
			appState: String(appStateForLighting),
			hasTx: String(hasTransaction),
			txState: String(transactionState),
			detail: String(openDetailDishTypeId),
			allergens: fmtIds(allergens),
			filtered: fmtIds(filteredDishTypeIds),
		};
		const prevSig = prevDesiredSigRef.current;
		const prevInputs = prevInputsRef.current;
		if (prevSig !== null && prevSig !== desiredSig) {
			const drivers = prevInputs
				? Object.keys(curInputs)
						.filter((key) => prevInputs[key] !== curInputs[key])
						.map((key) => `${key}:${prevInputs[key]}->${curInputs[key]}`)
						.join(",") || "none"
				: "init";
			lcLog("DESIRED-CHANGE", { run: run, from: prevSig, to: desiredSig, drivers: drivers });
		}
		prevDesiredSigRef.current = desiredSig;
		prevInputsRef.current = curInputs;

		if (desired.kind === "be-authoritative") {
			// The BE owns lighting while a transaction is Finished. Do NOT clear the optimistic
			// sent-refs here: clearing them made the next fe-driven tick re-issue an IDENTICAL command
			// (redundant mutation spam — ~31% of all fires in production logs). The refs are still
			// reconciled against the BE echo (echo-match, below) once control returns, so a genuine
			// divergence after Finished still fires; only verbatim repeats are suppressed.
			return;
		}

		// Clear the pending mode marker once the echo has caught up, so it can never wedge the
		// next genuine transition.
		if (sentModeRef.current != null && actualMode === sentModeRef.current) {
			lcLog("REF-CLEARED", { run: run, cause: "echo-match", channel: "mode", prev: String(sentModeRef.current) });
			sentModeRef.current = null;
		}

		const modeDiverges = actualMode == null || desired.mode !== actualMode;
		// Suppress only a repeat send of the SAME target we are already awaiting an echo for (or
		// while the mutation is genuinely in flight). A change to a NEW target is never blocked.
		const modeAlreadyInFlight = sentModeRef.current === desired.mode || modeLoading;

		if (modeDiverges && !modeAlreadyInFlight) {
			const sentBefore = sentModeRef.current;
			const redundant = lastIssuedModeRef.current === desired.mode;
			sentModeRef.current = desired.mode; // optimistic write BEFORE the mutation
			lastIssuedModeRef.current = desired.mode;
			lcLog("FIRE-SWITCH", {
				run: run,
				from: String(actualMode),
				to: desired.mode,
				sentBefore: String(sentBefore),
				redundant: redundant,
			});
			setMode({
				variables: {
					input: {
						id: fridgeId,
						lighting: {
							mode: desired.mode,
						},
					},
				},
			});
		}

		// Per-dish spotlight reconciliation is only meaningful in Active mode. In StandBy
		// the fridge ignores per-dish state, and on the current BE sending a per-dish
		// payload wakes the fridge back to Active — undoing the StandBy command. Skip the send,
		// but do NOT clear sentSpotlightRef: keeping it across the StandBy dip stops the return to
		// Active (e.g. an idle→active presence flap) from re-issuing an identical TOGGLE.
		if (desired.mode !== LightingModeType.Active) {
			return;
		}

		// Skip spotlight mutation if there is no per-dish payload to build —
		// even when actualSpotlightDishTypeIds is null (cold start), sending
		// an empty payload is pointless.
		if (availableDishTypeIds.size === 0) {
			return;
		}

		// Clear the pending spotlight marker once the echo matches what we sent.
		if (
			sentSpotlightRef.current != null &&
			actualSpotlightDishTypeIds != null &&
			shallowEqual(sentSpotlightRef.current, actualSpotlightDishTypeIds)
		) {
			lcLog("REF-CLEARED", {
				run: run,
				cause: "echo-match",
				channel: "spotlight",
				prev: fmtIds(sentSpotlightRef.current),
			});
			sentSpotlightRef.current = null;
		}

		const spotlightDiverges =
			actualSpotlightDishTypeIds == null || !shallowEqual(desired.spotlightDishTypeIds, actualSpotlightDishTypeIds);
		// Same in-flight de-dupe as the mode channel: don't re-send the identical per-dish payload
		// while the previous one is still awaiting its echo.
		const spotlightAlreadyInFlight =
			(sentSpotlightRef.current != null && shallowEqual(sentSpotlightRef.current, desired.spotlightDishTypeIds)) ||
			spotlightLoading;

		if (spotlightDiverges && !spotlightAlreadyInFlight) {
			const sentBefore = sentSpotlightRef.current;
			const redundant =
				lastIssuedSpotlightRef.current != null &&
				shallowEqual(lastIssuedSpotlightRef.current, desired.spotlightDishTypeIds);
			sentSpotlightRef.current = desired.spotlightDishTypeIds; // optimistic write BEFORE the mutation
			lastIssuedSpotlightRef.current = desired.spotlightDishTypeIds;
			const desiredSet = new Set(desired.spotlightDishTypeIds);
			const types = Array.from(availableDishTypeIds).map((id) =>
				desiredSet.has(id) ? createSpotlightLight(id) : createDefaultLight(id),
			);
			lcLog("FIRE-TOGGLE", {
				run: run,
				target: fmtIds(desired.spotlightDishTypeIds),
				fromActual: fmtIds(actualSpotlightDishTypeIds),
				sentBefore: fmtIds(sentBefore),
				redundant: redundant,
				payloadCount: types.length,
			});
			setSpotlight({
				variables: {
					input: {
						id: fridgeId,
						dishes: {
							types: types,
						},
					},
				},
			});
		}
	}, [
		desired,
		actualMode,
		actualSpotlightDishTypeIds,
		availableDishTypeIds,
		fridgeId,
		setMode,
		setSpotlight,
		modeLoading,
		spotlightLoading,
		// Diagnostic-only deps: already covered transitively by `desired`, listed so the tick log's
		// raw-input fields satisfy exhaustive-deps. Remove together with LIGHTING_DEBUG.
		appState,
		appStateForLighting,
		hasTransaction,
		transactionState,
		openDetailDishTypeId,
		allergens,
		filteredDishTypeIds,
	]);
};
